import axios from 'axios'
import { Base64 } from 'js-base64'
import { cloneDeep } from '../../utils'
import ResponseParserChain, { type ResponseParser } from '../ResponseParserChain'
import ErrorHandlerChain, { type ErrorHandler } from '../ErrorHandlerChain'
import RequestFilterChain, { type RequestFilter } from '../RequestFilterChain'
import { ElLoading } from 'element-plus'

import ResponseError from '../error/ResponseError'

export type RequestParams = Record<string, unknown>

export interface RequestOptionsI {
  loading?: boolean
  mask?: boolean
  timeout?: number
  headers?: Record<string, string>
}

//////////////////////////////////// Chain
let gRequestFilters: RequestFilter[] = []
let gResponseParsers: ResponseParser[] = []
let gErrorHandlers: ErrorHandler[] = []

//////////////////////////////////// GLOBAL VARS

let BASE_URL = '/'
let REQUEST_TIMEOUT = 1000 * 30
const LOADING_DELAY = 1500
let RESPONSE_TYPE: 'json' | 'text' = 'json'

//////////////////////////////////// AXIOS

const g_AxiosInstance = axios.create()
g_AxiosInstance.interceptors.request.use(function (config) {
  const chain = new RequestFilterChain(gRequestFilters)
  chain.doFilter(config, chain)
  return config
})
g_AxiosInstance.interceptors.response.use(function (response) {
  try {
    const chain = new ResponseParserChain(gResponseParsers)
    return chain.doParse(response, chain)
  } catch (e) {
    throw new ResponseError((e as Error).message, { cause: e }, response)
  }
})

export function request<T>(
  url: string,
  data: RequestParams | FormData = {},
  options: RequestOptionsI = { loading: true, mask: false, timeout: REQUEST_TIMEOUT }
): Promise<T> {
  let newData = data
  if (!(data instanceof FormData)) {
    newData = cloneDeep(data)
  }

  // loading
  let loading: any

  if (options.loading === true) {
    loading = ElLoading.service({ fullscreen: true, background: 'transparent' })
  }

  return g_AxiosInstance
    .request({
      baseURL: BASE_URL,
      method: 'POST',
      timeout: REQUEST_TIMEOUT,
      withCredentials: true,
      url,
      data: newData,
      responseType: RESPONSE_TYPE,
      ...options,
    })
    .then((data) => {
      if (loading) {
        loading.close()
      }

      return data
    })
    .catch(function (error) {
      if (loading) {
        loading.close()
      }

      const chain = new ErrorHandlerChain(gErrorHandlers)
      return chain.handle(error, chain)
    }) as Promise<T>
}

export function download(url: string, data: RequestParams = {}, options: RequestOptionsI = { loading: true, mask: false, timeout: REQUEST_TIMEOUT }) {
  let loading: any

  if (options.loading === true) {
    loading = ElLoading.service({ fullscreen: true, background: 'transparent' })
  }

  return g_AxiosInstance
    .request({
      baseURL: BASE_URL,
      method: 'POST',
      timeout: REQUEST_TIMEOUT,
      withCredentials: true,
      url,
      data,
      responseType: 'blob',
      ...options,
    })
    .then((data) => {
      if (loading) {
        loading.close()
      }

      return data
    })
    .then((response) => {
      const contentDisposition = response.headers['content-disposition']
      const blob = response.data

      const base64FileName = contentDisposition.match(/attachment; filename=\"(.*?)\"/)[1]
      const decodedFileName = Base64.decode(base64FileName)
      // for IE
      //@ts-ignore
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        //@ts-ignore
        window.navigator.msSaveOrOpenBlob(blob, decodedFileName)
      } else {
        const a = document.createElement('a')
        const url = URL.createObjectURL(blob)
        a.href = url
        a.download = decodedFileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }
    })
    .catch(function (error) {
      if (loading) {
        loading.close()
      }

      const chain = new ErrorHandlerChain(gErrorHandlers)
      return chain.handle(error, chain)
    })
}

export function upload(url: string, params: RequestParams, options: RequestOptionsI) {
  const formdata = new FormData()
  if (params) {
    const keys = Object.keys(params)
    const normalParams: Record<string, unknown> = {}

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      const itemValue = params[key]
      if (itemValue instanceof File) {
        formdata.append(key, itemValue)
      } else if (itemValue instanceof Array) {
        let isFile = false
        for (let j = 0; j < itemValue.length; j++) {
          if (itemValue[j] instanceof File) {
            isFile = true
            break
          }
        }
        if (isFile) {
          for (let j = 0; j < itemValue.length; j++) {
            formdata.append(key, itemValue[j])
          }
        } else {
          // formdata.append(key, JSON.stringify(itemValue))
          formdata.append(key, itemValue.join(','))
        }
      } else {
        // formdata.append(key, JSON.stringify(itemValue))
        formdata.append(key, itemValue as any)
      }
    }
  }

  return request(url, formdata, options)
}

// config
export const config = {
  setBaseUrl: (pBaseUrl: string) => {
    if (!pBaseUrl) {
      throw new Error(`pBaseUrl is null!`)
    }
    BASE_URL = pBaseUrl
  },
  setRequestTimeout: (pTimeout: number) => {
    REQUEST_TIMEOUT = pTimeout
  },
  setResponseType: (pType: 'json' | 'text') => {
    RESPONSE_TYPE = pType
  },
  setRequestFilters: (...filters: RequestFilter[]) => {
    if (filters == null) {
      throw new Error(`filters is null!`)
    }
    gRequestFilters = filters
  },
  setResponseParsers: (...parsers: ResponseParser[]) => {
    if (parsers == null) {
      throw new Error(`parsers is null!`)
    }
    gResponseParsers = parsers
  },
  setErrorHandlers: (...handlers: ErrorHandler[]) => {
    if (handlers == null) {
      throw new Error(`handlers should not null!`)
    }
    gErrorHandlers = handlers
  },
}
